import { useState, useEffect, ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Loader2 } from 'lucide-react';
import { Logo } from './Logo';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'nac_access_granted';
const TOKEN_KEY = 'nac_access_token';
const EXPIRY_KEY = 'nac_access_expires';

interface PasswordGateProps {
  children: ReactNode;
}

export function PasswordGate({ children }: PasswordGateProps) {
  const [accessGranted, setAccessGranted] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    // Check localStorage on mount
    const checkStoredAccess = () => {
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedExpiry = localStorage.getItem(EXPIRY_KEY);
      
      if (storedToken && storedExpiry) {
        const expiryTime = parseInt(storedExpiry, 10);
        if (Date.now() < expiryTime) {
          setAccessGranted(true);
        } else {
          // Token expired, clear storage
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(EXPIRY_KEY);
        }
      }
      setIsLoading(false);
    };
    
    checkStoredAccess();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(false);
    setIsValidating(true);

    try {
      // Validate password server-side
      const { data, error: fnError } = await supabase.functions.invoke('validate-access', {
        body: { password }
      });

      if (fnError) {
        console.error('Validation error:', fnError);
        setError(true);
        setPassword('');
        return;
      }

      if (data?.valid && data?.accessToken) {
        // Store the server-signed token
        localStorage.setItem(STORAGE_KEY, 'true');
        localStorage.setItem(TOKEN_KEY, data.accessToken);
        localStorage.setItem(EXPIRY_KEY, String(data.expiresAt));
        setAccessGranted(true);
      } else {
        setError(true);
        setPassword('');
      }
    } catch (err) {
      console.error('Validation request failed:', err);
      setError(true);
      setPassword('');
    } finally {
      setIsValidating(false);
    }
  };

  // Show nothing while checking localStorage
  if (isLoading) {
    return null;
  }

  // Access granted - render the app
  if (accessGranted) {
    return <>{children}</>;
  }

  // Show password form
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background">
      <div className="w-full max-w-md px-6">
        <div className="flex flex-col items-center space-y-8">
          {/* Logo */}
          <div className="flex flex-col items-center space-y-4">
            <Logo size="lg" />
            <p className="text-sm text-muted-foreground text-center">
              Kommer snart! - Ange lösenord för pre-access
            </p>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="w-full space-y-4">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="password"
                placeholder="Ange lösenord för att fortsätta"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(false);
                }}
                className="pl-10"
                autoFocus
                disabled={isValidating}
                maxLength={100}
              />
            </div>

            {error && (
              <p className="text-sm text-destructive text-center">
                Fel lösenord. Försök igen.
              </p>
            )}

            <Button type="submit" className="w-full" disabled={isValidating}>
              {isValidating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifierar...
                </>
              ) : (
                'Fortsätt'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
